import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';

/**
 * Main 3D Viewport engine managing scene, camera, PMREM IBL environments, exposure, and controls.
 */

export class Viewport {
  constructor(canvasEl) {
    this.canvas = canvasEl;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
      failIfMajorPerformanceCaveat: false
    });

    this.currentMeshGroup = null;
    this.gridHelper = null;
    this.autoRotate = false;
    this.isContextLost = false;

    // WebGL Context Loss / Restore event listeners for mobile Safari tab backgrounding
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('WebGL context lost. Pausing render loop.');
      this.isContextLost = true;
    }, false);

    this.canvas.addEventListener('webglcontextrestored', () => {
      console.warn('WebGL context restored. Rebuilding WebGL resources.');
      this.isContextLost = false;
      this.setupPMREM();
      this.setupLights();
    }, false);

    this.setupRenderer();
    this.setupCamera();
    this.setupPMREM();
    this.setupLights();
    this.setupGrid();
    
    window.addEventListener('resize', () => this.onWindowResize());
  }

  setupRenderer() {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(this.canvas.parentElement.clientWidth, this.canvas.parentElement.clientHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
  }

  setExposure(val) {
    this.currentEnvIntensity = val;
    if (this.scene) {
      this.scene.environmentIntensity = val;
    }
  }

  setupCamera() {
    this.camera.position.set(0, 2.5, 6);
    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI; // Unrestricted 360 degree rotation (polar angle up to Math.PI)
    this.controls.target.set(0, 1.2, 0);
    this.controls.mouseButtons.RIGHT = null; // Disable right click pan to allow right-click light rotation
    this.controls.update();

    this.setupEnvironmentRotation();
  }

  setupPMREM() {
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.pmremGenerator.compileEquirectangularShader();
    this.envCache = new Map();

    // Create crisp procedural studio environment fallback for mobile WebGL resilience
    try {
      const roomEnv = new RoomEnvironment();
      this.fallbackEnvTex = this.pmremGenerator.fromScene(roomEnv).texture;
      roomEnv.dispose();
    } catch (err) {
      console.warn('Procedural RoomEnvironment fallback failed:', err);
      this.fallbackEnvTex = null;
    }
  }

  fitCameraToBoundingBox(size, radius) {
    this.onWindowResize();

    const centerY = size && size.y > 0 ? size.y / 2 : 1.2;
    this.controls.target.set(0, centerY, 0);

    const fovRad = (this.camera.fov * Math.PI) / 180;
    
    // Calculate aspect ratio to avoid model cutting off on vertical/mobile screens
    const width = this.canvas.parentElement ? this.canvas.parentElement.clientWidth : window.innerWidth;
    const height = this.canvas.parentElement ? this.canvas.parentElement.clientHeight : window.innerHeight;
    const aspect = width / height;

    let effectiveFovRad = fovRad;
    if (aspect < 1) {
      // Horizontal FOV is smaller in portrait, calculate it
      effectiveFovRad = 2 * Math.atan(Math.tan(fovRad / 2) * aspect);
    }

    const modelRad = radius > 0 ? radius : 1.5;
    
    // Compute distance to fit bounding sphere with ~15% extra padding
    let dist = (modelRad * 1.15) / Math.sin(effectiveFovRad / 2);
    
    // Prevent clipping near plane or zooming out too far
    dist = Math.max(modelRad * 1.3, Math.min(1000.0, dist));

    this.camera.position.set(0, centerY + modelRad * 0.35, dist);
    this.controls.update();
  }

  setCameraDistance(dist) {
    const target = this.controls.target;
    const currentPos = this.camera.position;
    const dir = new THREE.Vector3().subVectors(currentPos, target);
    if (dir.lengthSq() < 0.0001) {
      dir.set(0, 0.4, 1);
    }
    dir.normalize();
    this.camera.position.copy(target).addScaledVector(dir, dist);
    this.controls.update();
  }

  setupLights() {
    this.lightGroup = new THREE.Group();
    this.lightGroup.name = 'LightPresetsGroup';

    // Add soft ambient & key lights as a fallback safety layer so models are never pitch black
    const ambient = new THREE.AmbientLight(0xffffff, 0.4);
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.8);
    keyLight.position.set(6, 12, 8);
    this.lightGroup.add(ambient, keyLight);

    this.scene.add(this.lightGroup);

    this.setLightingPreset('studio');
  }

  setLightingPreset(presetKey) {
    this.currentPresetKey = presetKey;

    // Check if HDRI is already cached in memory
    if (this.envCache && this.envCache.has(presetKey)) {
      const cachedEnv = this.envCache.get(presetKey);
      this.applyEnvironmentTexture(cachedEnv, presetKey);
      return;
    }

    // Apply procedural fallback immediately while loading/parsing EXR
    if (this.fallbackEnvTex) {
      this.applyEnvironmentTexture(this.fallbackEnvTex, presetKey);
    } else {
      this.scene.background = new THREE.Color(0x0e1115);
    }

    // Load .exr file asynchronously from public/ibl/
    const url = `ibl/${presetKey}.exr`;
    const exrLoader = new EXRLoader();
    exrLoader.setDataType(THREE.HalfFloatType);

    exrLoader.load(
      url,
      (texture) => {
        let envMap = null;
        try {
          envMap = this.pmremGenerator.fromEquirectangular(texture).texture;
          if (envMap) {
            this.envCache.set(presetKey, envMap);
          }
        } catch (err) {
          console.warn(`PMREM generation failed for ${url}, using fallback studio environment:`, err);
          envMap = this.fallbackEnvTex;
        } finally {
          if (texture && texture.dispose) {
            texture.dispose();
          }
        }

        // Apply env map if the user hasn't switched presets in the meantime
        if (this.currentPresetKey === presetKey && envMap) {
          this.applyEnvironmentTexture(envMap, presetKey);
        }
      },
      undefined,
      (err) => {
        console.warn(`Could not load IBL EXR map ${url}, using fallback environment:`, err);
        if (this.currentPresetKey === presetKey && this.fallbackEnvTex) {
          this.applyEnvironmentTexture(this.fallbackEnvTex, presetKey);
        }
      }
    );
  }

  applyEnvironmentTexture(envTex, presetKey) {
    if (envTex) {
      this.scene.environment = envTex;
    } else if (this.fallbackEnvTex) {
      this.scene.environment = this.fallbackEnvTex;
    }
    if (this.currentEnvIntensity !== undefined) {
      this.scene.environmentIntensity = this.currentEnvIntensity;
    }
    // Transparent 3D background showing the dark-gray CSS studio gradient behind the scene
    this.scene.background = null;
  }

  setupGrid() {
    this.gridHelper = new THREE.GridHelper(20, 20, 0x00f3ff, 0x222a35);
    this.gridHelper.position.y = -0.01;
    this.scene.add(this.gridHelper);
  }

  setGridVisible(visible) {
    if (this.gridHelper) {
      this.gridHelper.visible = visible;
    }
  }

  setAutoRotate(enabled) {
    this.autoRotate = enabled;
    this.controls.autoRotate = enabled;
    this.controls.autoRotateSpeed = 2.0;
  }

  resetCamera() {
    this.camera.position.set(0, 2.5, 6);
    this.controls.target.set(0, 1.2, 0);
    this.controls.update();
  }

  setModelMesh(object3D) {
    if (this.currentMeshGroup) {
      this.scene.remove(this.currentMeshGroup);
    }

    if (object3D) {
      this.currentMeshGroup = object3D;
      this.scene.add(this.currentMeshGroup);
    }
  }

  onWindowResize() {
    if (!this.canvas.parentElement) return;
    const width = this.canvas.parentElement.clientWidth;
    const height = this.canvas.parentElement.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  precompileLODs(lodMap, availableLevels) {
    if (!this.renderer || !lodMap || !availableLevels || this.isContextLost) return;

    try {
      const tempScene = new THREE.Scene();
      tempScene.environment = this.scene.environment || this.fallbackEnvTex;

      let lightClone = null;
      if (this.lightGroup) {
        lightClone = this.lightGroup.clone(true);
        tempScene.add(lightClone);
      }

      // Precompile active level to prevent GPU memory spike on mobile WebKit
      const primaryLevel = availableLevels[0] !== undefined ? availableLevels[0] : 0;
      const primaryMesh = lodMap.get(primaryLevel);
      if (primaryMesh) {
        tempScene.add(primaryMesh);
        this.renderer.compile(tempScene, this.camera);
        tempScene.remove(primaryMesh);
      }

      if (lightClone) {
        tempScene.remove(lightClone);
      }
    } catch (err) {
      console.warn('Precompilation skipped due to mobile WebGL limits:', err);
    }
  }

  setupEnvironmentRotation() {
    this.lightRotationAngle = 0;
    this.lightRotationAngle = 0;
    this.lightCameraOffset = 0;
    this.autoRotateLight = false;
    this.lightFollowsCamera = false;
    
    // Prevent right click context menu on canvas
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    let isDragging = false;
    let lastX = 0;

    this.canvas.addEventListener('mousedown', (e) => {
      if (e.button === 2) {
        isDragging = true;
        lastX = e.clientX;
        e.preventDefault();
      }
    });

    window.addEventListener('mousemove', (e) => {
      if (isDragging) {
        const deltaX = e.clientX - lastX;
        lastX = e.clientX;
        this.rotateLightByDelta(deltaX * 0.005);
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (e.button === 2) {
        isDragging = false;
      }
    });

    // Touch handlers for mobile (3-finger gesture)
    let isThreeFingerDragging = false;
    let lastTouchX = 0;

    this.canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length === 3) {
        isThreeFingerDragging = true;
        lastTouchX = (e.touches[0].clientX + e.touches[1].clientX + e.touches[2].clientX) / 3;
        e.preventDefault();
      }
    }, { passive: false });

    this.canvas.addEventListener('touchmove', (e) => {
      if (e.touches.length === 3 && isThreeFingerDragging) {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        const currentTouchX = (e.touches[0].clientX + e.touches[1].clientX + e.touches[2].clientX) / 3;
        const deltaX = currentTouchX - lastTouchX;
        lastTouchX = currentTouchX;

        this.rotateLightByDelta(deltaX * 0.005);
      }
    }, { passive: false });

    this.canvas.addEventListener('touchend', (e) => {
      if (e.touches.length < 3) {
        isThreeFingerDragging = false;
      }
    });
  }

  getCamAngle() {
    if (!this.camera || !this.controls) return 0;
    const camDirX = this.camera.position.x - this.controls.target.x;
    const camDirZ = this.camera.position.z - this.controls.target.z;
    return Math.atan2(camDirX, camDirZ);
  }

  rotateLightByDelta(deltaAngle) {
    if (this.lightFollowsCamera) {
      this.lightCameraOffset += deltaAngle;
      this.lightRotationAngle = this.getCamAngle() + this.lightCameraOffset;
    } else {
      this.lightRotationAngle += deltaAngle;
    }
  }

  setLightRotation(angle) {
    if (this.lightGroup) {
      this.lightGroup.rotation.y = angle;
    }
    if (this.scene) {
      if (this.scene.environmentRotation) {
        this.scene.environmentRotation.y = angle;
      }
      if (this.scene.backgroundRotation) {
        this.scene.backgroundRotation.y = angle;
      }
    }
  }

  setAutoRotateLight(enabled) {
    this.autoRotateLight = enabled;
  }

  setLightFollowsCamera(enabled) {
    const camAngle = this.getCamAngle();
    if (enabled && !this.lightFollowsCamera) {
      // Transitioning ON: preserve current world angle, compute camera relative offset
      this.lightCameraOffset = this.lightRotationAngle - camAngle;
      this.lightFollowsCamera = true;
    } else if (!enabled && this.lightFollowsCamera) {
      // Transitioning OFF: preserve current world angle cleanly
      this.lightRotationAngle = camAngle + this.lightCameraOffset;
      this.lightFollowsCamera = false;
    }
  }

  render() {
    if (this.isContextLost) return;

    this.controls.update();

    if (this.autoRotateLight) {
      this.rotateLightByDelta(0.005);
    }

    let finalAngle = this.lightRotationAngle;
    if (this.lightFollowsCamera) {
      finalAngle = this.getCamAngle() + this.lightCameraOffset;
    }

    this.setLightRotation(finalAngle);

    this.renderer.render(this.scene, this.camera);
  }
}
